import { useCallback, useEffect, useRef, useState } from "react";
import { primerPaymentService } from "../utils/primer/primerPaymentService";
import { useTemplateStore } from "@/zustand/templateStore";

const PRIMER_SDK_URL = "https://sdk.primer.io/web/v2.53.0/Primer.min.js";

/**
 * Parse field-level errors from Primer API 422 response body.
 * Maps API validation error paths to card form fields.
 */
function parseFieldErrorsFromResponse(responseBody) {
  const result = { cardNumber: null, expiry: null, cvv: null, messages: [] };
  try {
    const validationErrors = responseBody?.error?.validationErrors;
    if (!Array.isArray(validationErrors) || validationErrors.length === 0)
      return result;
    const errors = validationErrors[0]?.errors;
    if (!Array.isArray(errors)) return result;
    for (const err of errors) {
      const path = (err.path || "").toLowerCase();
      const desc = err.description || "";
      if (
        !desc ||
        desc.includes("Missing required field") ||
        desc.includes("Unexpected extra field")
      )
        continue;
      const displayMsg = desc.charAt(0).toUpperCase() + desc.slice(1);
      if (
        path.includes("paymentinstrument.number") ||
        path.includes("cardnumber")
      ) {
        if (!result.cardNumber) {
          result.cardNumber = displayMsg;
          result.messages.push(displayMsg);
        }
      } else if (
        path.includes("expirationmonth") ||
        path.includes("expirationyear") ||
        path.includes("expiry")
      ) {
        if (!result.expiry) {
          result.expiry = displayMsg;
          result.messages.push(displayMsg);
        }
      } else if (path.includes("cvv") || path.includes("cvc")) {
        if (!result.cvv) {
          result.cvv = displayMsg;
          result.messages.push(displayMsg);
        }
      }
    }
  } catch (e) {}
  return result;
}

export function usePrimerCheckout(options = {}) {
  const { onSuccess, onError, on3DSRequired } = options;

  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clientToken, setClientToken] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [hostedInputsReady, setHostedInputsReady] = useState(false);
  const isSubmittingRef = useRef(false);
  const paymentCompletedRef = useRef(false);
  const tokenizationStartedRef = useRef(false);
  const [cardNumberValid, setCardNumberValid] = useState(null);
  const [expiryValid, setExpiryValid] = useState(null);
  const [cvvValid, setCvvValid] = useState(null);
  const [cardNumberError, setCardNumberError] = useState(null);
  const [expiryError, setExpiryError] = useState(null);
  const [cvvError, setCvvError] = useState(null);

  // Refs to track validation state for use inside useCallback without stale closures
  const cardNumberValidRef = useRef(null);
  const expiryValidRef = useRef(null);
  const cvvValidRef = useRef(null);

  const primerInstanceRef = useRef(null);
  const cardManagerRef = useRef(null);
  const hostedInputsRef = useRef(null);
  const sdkLoadedRef = useRef(false);
  const pendingOrderDataRef = useRef(null);
  const sessionOrderIdRef = useRef(null);
  const [detectedCardNetwork, setDetectedCardNetwork] = useState(null);
  const detectedCardNetworkRef = useRef(null);
  const lastRenderConfigRef = useRef(null);
  const lastWidthRef = useRef(null);
  const [expectedCardNumberLength, setExpectedCardNumberLength] =
    useState(null);
  const submitErrorHandledRef = useRef(false);
  const lastPaymentInstrumentsErrorRef = useRef(null);

  // Install XHR interceptor once to capture 422 response body from payment-instruments.
  // Registered in open() so our load listener fires BEFORE the SDK's own handler.
  useEffect(() => {
    const origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      if (
        typeof url === "string" &&
        url.includes("payment-instruments")
      ) {
        this.addEventListener("load", function () {
          if (this.status === 422) {
            try {
              lastPaymentInstrumentsErrorRef.current = JSON.parse(
                this.responseText,
              );
            } catch (e) {}
          }
        });
      }
      return origOpen.apply(this, arguments);
    };
    return () => {
      XMLHttpRequest.prototype.open = origOpen;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (cardManagerRef.current && cardManagerRef.current.removeHostedInputs) {
        try {
          cardManagerRef.current.removeHostedInputs();
        } catch (e) {}
      }
      hostedInputsRef.current = null;
      cardManagerRef.current = null;
      primerInstanceRef.current = null;
      pendingOrderDataRef.current = null;
    };
  }, []);

  const loadPrimerSDK = useCallback(async () => {
    if (sdkLoadedRef.current || window.Primer) {
      return;
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = PRIMER_SDK_URL;
      script.async = true;
      script.onload = () => {
        sdkLoadedRef.current = true;
        resolve();
      };
      script.onerror = () => {
        reject(new Error("Failed to load Primer SDK"));
      };
      document.head.appendChild(script);
    });
  }, []);

  const initializeSession = useCallback(
    async (orderData) => {
      setIsLoading(true);
      setError(null);

      try {
        await loadPrimerSDK();

        const session = await primerPaymentService.createSession(orderData);
        setClientToken(session.clientToken);
        setOrderId(session.orderId);
        sessionOrderIdRef.current = session.orderId;

        if (window.Primer && session.clientToken) {
          const primer = await window.Primer.createHeadless(
            session.clientToken,
            {
            apiVersion: '2.4',
            paymentHandling: "MANUAL",
            onAvailablePaymentMethodsLoad: () => {},
            onCheckoutComplete: (payment) => {
              setIsLoading(false);
              if (onSuccess) {
                onSuccess(payment.orderId || sessionOrderIdRef.current);
              }
            },
            onCheckoutFail: (err) => {
              setIsLoading(false);
              isSubmittingRef.current = false;
              const errorMsg =
                err?.message ||
                err?.description ||
                err?.errorId ||
                "Payment failed";
              setError(errorMsg);
              if (onError) {
                onError(errorMsg);
              }
            },
            onTokenizeError: (err) => {
              submitErrorHandledRef.current = true;
              isSubmittingRef.current = false;
              setIsLoading(false);
              useTemplateStore.getState().setLoadingState(false);

              let errorMsg =
                "Card validation failed. Please check your card details.";

              // Extract field-level errors from captured 422 response
              const captured = lastPaymentInstrumentsErrorRef.current;
              if (captured) {
                const fieldErrors = parseFieldErrorsFromResponse(captured);
                if (fieldErrors.cardNumber) {
                  setCardNumberError(fieldErrors.cardNumber);
                  setCardNumberValid(false);
                  cardNumberValidRef.current = false;
                }
                if (fieldErrors.expiry) {
                  setExpiryError(fieldErrors.expiry);
                  setExpiryValid(false);
                  expiryValidRef.current = false;
                }
                if (fieldErrors.cvv) {
                  setCvvError(fieldErrors.cvv);
                  setCvvValid(false);
                  cvvValidRef.current = false;
                }
                if (fieldErrors.messages.length > 0) {
                  errorMsg = fieldErrors.messages.join(". ");
                }
              } else if (typeof err === "string") {
                errorMsg = err;
              } else if (err?.data?.validationErrors?.length > 0) {
                const sdkFieldErrors = err.data.validationErrors
                  .map((e) => e.message || e.errorMessage || e.description)
                  .filter(Boolean);

                sdkFieldErrors.forEach((msg) => {
                  const lower = msg.toLowerCase();
                  if (lower.includes("card number")) {
                    setCardNumberError(msg);
                    setCardNumberValid(false);
                    cardNumberValidRef.current = false;
                  } else if (lower.includes("expir")) {
                    setExpiryError(msg);
                    setExpiryValid(false);
                    expiryValidRef.current = false;
                  } else if (lower.includes("cvv") || lower.includes("cvc") || lower.includes("security")) {
                    setCvvError(msg);
                    setCvvValid(false);
                    cvvValidRef.current = false;
                  }
                });

                errorMsg =
                  sdkFieldErrors.length > 0
                    ? sdkFieldErrors.join(". ")
                    : err?.message || errorMsg;
              } else if (err?.message) {
                // Don't show raw SDK/API messages to the user
                const rawMsg = err.message;
                if (!rawMsg.includes("primer.io/docs") && !rawMsg.includes("payload")) {
                  errorMsg = rawMsg;
                }
              }

              setError(errorMsg);
              if (onError) {
                onError(errorMsg);
              }
            },
            onTokenizeSuccess: async (paymentMethodTokenData, handler) => {
              try {
                tokenizationStartedRef.current = true;
                const token =
                  paymentMethodTokenData?.token ||
                  paymentMethodTokenData?.paymentMethodToken;

                if (!token) {
                  handler.handleFailure("No payment token received");
                  return;
                }
              } catch (earlyError) {
                try {
                  handler.handleFailure("Payment processing error");
                } catch (e) {}
                return;
              }

              const tokenData = paymentMethodTokenData;
              const token = tokenData?.token || tokenData?.paymentMethodToken;

              try {
                const orderDataToUse = pendingOrderDataRef.current;

                if (!orderDataToUse) {
                  handler.handleFailure("No order data available");
                  return;
                }

                handler.handleSuccess();

                setIsLoading(true);
                useTemplateStore.getState().setLoadingState(true);

                const productsToSend = orderDataToUse.products || {};

                const result =
                  await primerPaymentService.processPaymentWithToken(
                    token,
                    orderDataToUse.data || orderDataToUse,
                    productsToSend,
                    sessionOrderIdRef.current,
                  );

                if (
                  result.result === "SUCCESS" ||
                  result.result === "PENDING"
                ) {
                  paymentCompletedRef.current = true;
                  if (onSuccess) {
                    onSuccess(
                      result.message?.orderId || sessionOrderIdRef.current,
                    );
                  }
                } else if (
                  result.result === "PENDING_3DS" &&
                  result.redirectUrl
                ) {
                  if (on3DSRequired) {
                    on3DSRequired(result.redirectUrl);
                  } else {
                    window.location.href = result.redirectUrl;
                  }
                } else {
                  const errorMsg =
                    typeof result.message === "string"
                      ? result.message
                      : "Payment failed";
                  setError(errorMsg);
                  isSubmittingRef.current = false;
                  if (onError) {
                    onError(errorMsg);
                  }
                }
              } catch (err) {
                const msg =
                  err instanceof Error
                    ? err.message
                    : "Payment processing failed";
                setError(msg);
                isSubmittingRef.current = false;
                if (onError) {
                  onError(msg);
                }
              }
            },
          });

          primerInstanceRef.current = primer;

          await primer.start();

          try {
            const cardManager = await primer.createPaymentMethodManager(
              "PAYMENT_CARD",
              {
                onCardNetworksChange: (event) => {
                  const networks = event?.detectedCardNetworks?.items || [];
                  if (networks.length > 0) {
                    const firstNetwork = networks[0];
                    setDetectedCardNetwork(firstNetwork.network);
                    detectedCardNetworkRef.current = firstNetwork.network;
                    if (firstNetwork.cardNumberLength) {
                      setExpectedCardNumberLength(
                        firstNetwork.cardNumberLength,
                      );
                    }
                  }
                },
                onMetadataChange: (metadata) => {
                  if (metadata?.cardNumberLength) {
                    setExpectedCardNumberLength(metadata.cardNumberLength);
                  }
                },
              },
            );
            cardManagerRef.current = cardManager;

            if (cardManager && cardManager.createHostedInputs) {
              // Don't call createHostedInputs() here - DOM containers don't exist yet.
              // It will be called in renderCardInputs when containers are ready.
              setIsReady(true);
              return {
                clientToken: session.clientToken,
                isReady: true,
                orderId: session.orderId,
              };
            } else {
              setError("Card payments not available");
              return {
                clientToken: session.clientToken,
                isReady: false,
                orderId: session.orderId,
                error: "Card payments not available",
              };
            }
          } catch (cardError) {
            setError(
              "Card payments not available: " +
                (cardError?.message || "Unknown error"),
            );
            return {
              clientToken: session.clientToken,
              isReady: false,
              orderId: session.orderId,
              error: cardError?.message,
            };
          }
        }

        return { clientToken: null, isReady: false, orderId: null };
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to initialize Primer";
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
        return {
          clientToken: null,
          isReady: false,
          orderId: null,
          error: errorMessage,
        };
      } finally {
        setIsLoading(false);
      }
    },
    [loadPrimerSDK, onSuccess, onError, on3DSRequired],
  );

  const renderCardInputs = useCallback(async (containers, style = {}) => {
    // Create hosted inputs now that DOM containers are ready
    if (!hostedInputsRef.current && cardManagerRef.current?.createHostedInputs) {
      try {
        const hostedInputs = cardManagerRef.current.createHostedInputs();
        hostedInputsRef.current = hostedInputs;
      } catch (err) {
        setError("Failed to create card inputs");
        return false;
      }
    }

    if (!hostedInputsRef.current) {
      return false;
    }

    lastRenderConfigRef.current = { containers, style };
    lastWidthRef.current = window.innerWidth;

    const { cardNumberInput, expiryInput, cvvInput } = hostedInputsRef.current;

    const defaultStyle = {
      input: {
        base: {
          color: "#3c3c3c",
          fontFamily: '"Roboto", sans-serif',
          fontSize: "14px",
          fontWeight: "400",
          lineHeight: "20px",
          height: "100%",
          padding: "16px 12px",
          boxSizing: "border-box",
        },
        focus: { outline: "none" },
        error: { color: "#3c3c3c" },
        placeholder: { color: "#3c3c3c" },
      },
      container: {
        base: {
          height: "100%",
          borderRadius: "0",
          border: "none",
          background: "transparent",
        },
        focus: { border: "none", boxShadow: "none" },
      },
      ...style,
    };

    try {
      await Promise.all([
        cardNumberInput.render(containers.cardNumber, {
          placeholder: "Card Number",
          style: defaultStyle,
          onValidate: (result) => {
            setCardNumberValid(result.valid);
            cardNumberValidRef.current = result.valid;
            if (!result.valid && result.errors?.length > 0) {
              setCardNumberError(
                result.errors[0].message || "Invalid card number",
              );
            } else {
              setCardNumberError(null);
            }
          },
          onBlur: () => {},
        }),
        expiryInput.render(containers.expiry, {
          placeholder: "Expiration (MM/YY)",
          style: defaultStyle,
          onValidate: (result) => {
            setExpiryValid(result.valid);
            expiryValidRef.current = result.valid;
            if (!result.valid && result.errors?.length > 0) {
              setExpiryError(result.errors[0].message || "Invalid expiry date");
            } else {
              setExpiryError(null);
            }
          },
        }),
        cvvInput.render(containers.cvv, {
          placeholder: "CVV",
          style: defaultStyle,
          onValidate: (result) => {
            setCvvValid(result.valid);
            cvvValidRef.current = result.valid;
            if (!result.valid && result.errors?.length > 0) {
              setCvvError(result.errors[0].message || "Invalid CVV");
            } else {
              setCvvError(null);
            }
          },
        }),
      ]);

      setHostedInputsReady(true);
      return true;
    } catch (err) {
      setError("Failed to render card inputs");
      return false;
    }
  }, []);

  const refreshHostedInputs = useCallback(async () => {
    if (
      !lastRenderConfigRef.current ||
      !cardManagerRef.current ||
      !hostedInputsReady
    ) {
      return false;
    }

    try {
      if (cardManagerRef.current.removeHostedInputs) {
        cardManagerRef.current.removeHostedInputs();
      }

      if (cardManagerRef.current.createHostedInputs) {
        const hostedInputs = cardManagerRef.current.createHostedInputs();
        hostedInputsRef.current = hostedInputs;

        // Reset validation refs since inputs are recreated empty
        cardNumberValidRef.current = null;
        expiryValidRef.current = null;
        cvvValidRef.current = null;

        const { containers, style } = lastRenderConfigRef.current;
        const { cardNumberInput, expiryInput, cvvInput } = hostedInputs;

        const defaultStyle = {
          input: {
            base: {
              color: "#3c3c3c",
              fontFamily: '"Roboto", sans-serif',
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "20px",
              height: "100%",
              padding: "16px 12px",
              boxSizing: "border-box",
            },
            focus: { outline: "none" },
            error: { color: "#3c3c3c" },
            placeholder: { color: "#3c3c3c" },
          },
          container: {
            base: {
              height: "100%",
              borderRadius: "0",
              border: "none",
              background: "transparent",
            },
            focus: { border: "none", boxShadow: "none" },
          },
          ...style,
        };

        await Promise.all([
          cardNumberInput.render(containers.cardNumber, {
            placeholder: "Card Number",
            style: defaultStyle,
            onValidate: (result) => {
              setCardNumberValid(result.valid);
              cardNumberValidRef.current = result.valid;
              setCardNumberError(
                !result.valid && result.errors?.length > 0
                  ? result.errors[0].message
                  : null,
              );
            },
          }),
          expiryInput.render(containers.expiry, {
            placeholder: "Expiration (MM/YY)",
            style: defaultStyle,
            onValidate: (result) => {
              setExpiryValid(result.valid);
              expiryValidRef.current = result.valid;
              setExpiryError(
                !result.valid && result.errors?.length > 0
                  ? result.errors[0].message
                  : null,
              );
            },
          }),
          cvvInput.render(containers.cvv, {
            placeholder: "CVV",
            style: defaultStyle,
            onValidate: (result) => {
              setCvvValid(result.valid);
              cvvValidRef.current = result.valid;
              setCvvError(
                !result.valid && result.errors?.length > 0
                  ? result.errors[0].message
                  : null,
              );
            },
          }),
        ]);

        return true;
      }
    } catch (err) {}
    return false;
  }, [hostedInputsReady]);

  const setCardholderName = useCallback((name) => {
    if (cardManagerRef.current && cardManagerRef.current.setCardholderName) {
      cardManagerRef.current.setCardholderName(name);
    }
  }, []);

  const validateCardInputs = useCallback(async () => {
    if (!cardManagerRef.current) {
      return { valid: false, errors: ["Card manager not initialized"] };
    }

    try {
      const validation = await cardManagerRef.current.validate();
      return validation;
    } catch (err) {
      return { valid: false, errors: [err.message || "Validation failed"] };
    }
  }, []);

  const submitCardPayment = useCallback(
    async (orderData) => {
      if (isSubmittingRef.current) {
        return {
          result: "PENDING",
          message: { orderId: sessionOrderIdRef.current },
          errorCode: "ALREADY_SUBMITTING",
        };
      }

      if (paymentCompletedRef.current) {
        return {
          result: "SUCCESS",
          message: { orderId: sessionOrderIdRef.current },
          errorCode: "ALREADY_COMPLETED",
        };
      }

      // Clear previous errors on each new submit attempt
      setError(null);
      setCardNumberError(null);
      setExpiryError(null);
      setCvvError(null);

      if (!cardManagerRef.current) {
        const errorMsg =
          "Card payment not initialized. Call initializeSession first.";
        setError(errorMsg);
        setIsLoading(false);
        return {
          result: "ERROR",
          message: { orderId: undefined },
          errorCode: "NOT_INITIALIZED",
        };
      }

      if (!hostedInputsReady) {
        const errorMsg = "Card inputs not ready. Call renderCardInputs first.";
        setError(errorMsg);
        setIsLoading(false);
        return {
          result: "ERROR",
          message: { orderId: undefined },
          errorCode: "INPUTS_NOT_READY",
        };
      }

      try {
        const validation = await cardManagerRef.current.validate();
        if (!validation.valid) {
          const validationErrors = validation.validationErrors || [];
          const errorMessages = validationErrors
            .map((e) => e.message || e.errorMessage)
            .filter(Boolean);

          // Set individual field errors based on validation results
          validationErrors.forEach((e) => {
            const src = (e.source || e.field || "").toLowerCase();
            const msg = e.message || e.errorMessage || "Invalid";
            if (src.includes("cardnumber") || src.includes("card-number") || src.includes("card_number")) {
              setCardNumberError(msg);
              setCardNumberValid(false);
              cardNumberValidRef.current = false;
            } else if (src.includes("expiry") || src.includes("expiration")) {
              setExpiryError(msg);
              setExpiryValid(false);
              expiryValidRef.current = false;
            } else if (src.includes("cvv") || src.includes("cvc") || src.includes("security")) {
              setCvvError(msg);
              setCvvValid(false);
              cvvValidRef.current = false;
            }
          });

          // If no field-specific errors were matched, mark all as invalid
          if (validationErrors.length > 0 && !validationErrors.some((e) => e.source || e.field)) {
            errorMessages.forEach((msg) => {
              const lower = msg.toLowerCase();
              if (lower.includes("card number")) {
                setCardNumberError(msg);
                setCardNumberValid(false);
                cardNumberValidRef.current = false;
              } else if (lower.includes("expir")) {
                setExpiryError(msg);
                setExpiryValid(false);
                expiryValidRef.current = false;
              } else if (lower.includes("cvv") || lower.includes("cvc") || lower.includes("security")) {
                setCvvError(msg);
                setCvvValid(false);
                cvvValidRef.current = false;
              }
            });
          }

          const errorMsg = errorMessages.join(". ") || "Please check your card details.";
          setError(errorMsg);
          setIsLoading(false);
          return {
            result: "ERROR",
            message: errorMsg,
            errorCode: "VALIDATION_FAILED",
          };
        }

        isSubmittingRef.current = true;
        pendingOrderDataRef.current = orderData;
        tokenizationStartedRef.current = false;
        submitErrorHandledRef.current = false;
        lastPaymentInstrumentsErrorRef.current = null;

        // Catch SDK internal crashes (unhandled promise rejections)
        const sdkCrashHandler = (event) => {
          if (submitErrorHandledRef.current) return;
          const errMsg = event?.reason?.message || "";
          if (errMsg.includes("primer") || errMsg.includes("Primer") ||
              errMsg.includes("payment-instruments") || errMsg.includes("ApiController") ||
              (event?.reason?.name === "PrimerClientError")) {
            event.preventDefault();
            submitErrorHandledRef.current = true;
            isSubmittingRef.current = false;
            setIsLoading(false);
            useTemplateStore.getState().setLoadingState(false);
            const userMsg = "Card validation failed. Please check your card details.";
            setError(userMsg);
            if (onError) { onError(userMsg); }
          }
        };
        window.addEventListener("unhandledrejection", sdkCrashHandler);

        // Safety timeout: if no callback fires within 10s, clean up
        const safetyTimeout = setTimeout(() => {
          window.removeEventListener("unhandledrejection", sdkCrashHandler);
          if (!submitErrorHandledRef.current && isSubmittingRef.current) {
            isSubmittingRef.current = false;
            setIsLoading(false);
            useTemplateStore.getState().setLoadingState(false);
            const timeoutMsg = "Payment processing timed out. Please try again.";
            setError(timeoutMsg);
            if (onError) { onError(timeoutMsg); }
          }
        }, 10000);

        try {
          await cardManagerRef.current.submit();
        } catch (submitErr) {
          window.removeEventListener("unhandledrejection", sdkCrashHandler);
          clearTimeout(safetyTimeout);
          if (!submitErrorHandledRef.current) {
            submitErrorHandledRef.current = true;
            isSubmittingRef.current = false;
            const errorMsg = "Card validation failed. Please check your card details.";
            setError(errorMsg);
            setIsLoading(false);
            useTemplateStore.getState().setLoadingState(false);
            if (onError) { onError(errorMsg); }
          }
          return {
            result: "ERROR",
            message: "Card validation failed. Please check your card details.",
            errorCode: "SUBMIT_FAILED",
          };
        }

        // If onTokenizeError already fired during the await, return ERROR
        if (submitErrorHandledRef.current) {
          window.removeEventListener("unhandledrejection", sdkCrashHandler);
          clearTimeout(safetyTimeout);
          return {
            result: "ERROR",
            message: "Card validation failed. Please check your card details.",
            errorCode: "SUBMIT_FAILED",
          };
        }

        // Clean up after successful submit (callbacks will handle the rest)
        setTimeout(() => {
          window.removeEventListener("unhandledrejection", sdkCrashHandler);
          clearTimeout(safetyTimeout);
        }, 500);

        return {
          result: "PENDING",
          message: { orderId: sessionOrderIdRef.current },
        };
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : typeof err === "string"
              ? err
              : "Payment failed";
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
        setIsLoading(false);
        isSubmittingRef.current = false;
        return {
          result: "ERROR",
          message: errorMessage,
          errorCode: "SUBMIT_FAILED",
        };
      }
    },
    [hostedInputsReady, onError],
  );

  return {
    isReady,
    isLoading,
    error,
    clientToken,
    orderId,
    hostedInputsReady,
    detectedCardNetwork,
    expectedCardNumberLength,
    cardNumberValid,
    expiryValid,
    cvvValid,
    cardNumberError,
    expiryError,
    cvvError,
    initializeSession,
    renderCardInputs,
    setCardholderName,
    validateCardInputs,
    submitCardPayment,
    refreshHostedInputs,
  };
}

export default usePrimerCheckout;
